import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import navigationData from "../data/navigation.json";
import type { NavItem } from "../types";
import { cn } from "../lib/utils";
import { lockScroll } from "../hooks/useSmoothScroll";
import { Menu, Close } from "./Icon";
import ThemeToggle from "./ThemeToggle";

const items = navigationData.items as NavItem[];

const EASE = [0.22, 1, 0.36, 1] as const;

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string>("");

  // Only the home route has the dark video hero behind the transparent bar.
  // NotFound also renders inside Layout but on a light background, so the
  // light-on-dark treatment has to be gated on the route, not just on scroll.
  const overHero = useLocation().pathname === "/" && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which section is in view so the marker has something to follow.
  useEffect(() => {
    const sections = items
      .map((item) => document.querySelector(item.href))
      .filter((el): el is Element => Boolean(el));
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target.id) setActive(`#${visible.target.id}`);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Close the drawer on Escape, and lock scrolling while it is open.
  useEffect(() => {
    if (!open) return;
    lockScroll(true);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      lockScroll(false);
    };
  }, [open]);

  return (
    <>
      <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-colors duration-300",
        (scrolled || open) && "border-b border-border bg-background/85 backdrop-blur-md"
      )}
    >
      <div className="mx-auto flex h-20 max-w-screen-2xl items-center justify-between px-6">
        <a
          href="#top"
          className={cn(
            "u-label font-bold tracking-[0.18em] transition-colors",
            overHero ? "text-white" : "text-foreground"
          )}
        >
          Gabriel Nyante
        </a>

        <nav className="hidden items-center gap-10 md:flex" aria-label="Primary">
          {items.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={cn(
                "u-label relative py-2 transition-colors",
                overHero
                  ? active === item.href
                    ? "text-white"
                    : "text-white/65 hover:text-white"
                  : active === item.href
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
              )}
            >
              {item.label}
              {active === item.href && (
                <motion.span
                  layoutId="nav-active"
                  className={cn(
                    "absolute inset-x-0 -bottom-0.5 h-px",
                    overHero ? "bg-white" : "bg-primary"
                  )}
                  transition={{ duration: 0.4, ease: EASE }}
                />
              )}
            </a>
          ))}
          <ThemeToggle className={overHero ? "text-white hover:text-white/70" : undefined} />
        </nav>

        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle className={overHero ? "text-white hover:text-white/70" : undefined} />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            className={cn(
              "grid h-9 w-9 place-items-center transition-colors",
              overHero ? "text-white" : "text-foreground"
            )}
          >
            {open ? <Close className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ clipPath: "inset(0% 0% 100% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            exit={{ clipPath: "inset(0% 0% 100% 0%)" }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed inset-x-0 top-20 bottom-0 z-40 bg-background md:hidden"
          >
            <nav
              className="flex h-full flex-col gap-2 px-6 pt-10"
              aria-label="Mobile"
            >
              {items.map((item, i) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                  className="border-b border-border py-5 text-h2 font-bold uppercase"
                >
                  {item.label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
